import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { MarketLogo } from "@/components/ConnectPrimaryCard";
import { PageIntro } from "@/components/SellerToolsNav";
import { useAuth } from "@/context/AuthContext";
import { HELPER_BROWSER, HELPER_HOST, HIDDEN_SELLER_CHANNELS, accountLabel, beginConnect, helperOpenUrl, helperSeen, openHelperSite } from "@/lib/marketplaces";
import { suggestHsn } from "@/lib/marketForms";

const CHANNEL_TAGLINE = {
  ebay: "Auctions and fixed-price listings",
  etsy: "Handmade, vintage, and craft goods",
  depop: "Fashion resale",
  amazon: "Seller Central catalog and offers",
  amazon_in: "Seller Central India",
  walmart: "Marketplace catalog and inventory",
  flipkart: "Seller Hub listings",
  poshmark: "Social resale",
  mercari: "Peer-to-peer marketplace",
  grailed: "Menswear and designer resale",
  facebook: "Local and shipped listings",
  meesho: "Supplier catalog",
  myntra: "Fashion marketplace",
  ajio: "Fashion marketplace",
  nykaa: "Beauty and fashion",
  tatacliq: "Lifestyle marketplace",
  olx: "Classifieds",
  pinterest: "Product pins",
  aliexpress: "Global marketplace",
  woocommerce: "Your WooCommerce store",
  wix: "Your Wix store",
  bonanza: "Booth listings",
  backmarket: "Refurbished marketplace",
  stockx: "Sneakers and collectibles",
  goat: "Sneakers through Alias",
  discogs: "Music marketplace",
  abebooks: "Bookseller inventory",
  squarespace: "Your Squarespace store",
  square: "Your Square catalog",
  google_shopping: "Merchant Center products",
};

const COMING_SOON_CHANNELS = new Set([
  "myntra", "ajio", "nykaa", "tatacliq", "olx", "meesho", "amazon", "amazon_in", "google_shopping",
]);

function canConnect(c) {
  return Boolean(c && !c.native && (c.connect_via === "unified" || HELPER_BROWSER.has(c.id)));
}

function statusKind(c) {
  if (c?.native) return "connected";
  if (c?.last_error) return "error";
  if (c?.connected) return "connected";
  return "disconnected";
}

function StatusPill({ c }) {
  const kind = statusKind(c);
  const label = c?.native ? "Native" : kind === "error" ? "Error" : kind === "connected" ? "Connected" : "Not connected";
  const cls =
    kind === "connected"
      ? "bg-emerald-500/15 text-emerald-400"
      : kind === "error"
        ? "bg-red-500/15 text-red-400"
        : "bg-white/5 text-aura-muted";
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${kind === "connected" ? "bg-emerald-400" : kind === "error" ? "bg-red-400" : "bg-aura-muted"}`} />
      {label}
    </span>
  );
}

function connectorBadge(c) {
  if (c?.native) return "Native shop";
  if (c?.unified?.provider) {
    const name = c.connector || "Shoptosa";
    return c.unified.sandbox ? `${name} sandbox` : name;
  }
  if (c?.connect_via === "unified") return c.connector;
  if (c?.connect_via === "helper") {
    return HELPER_BROWSER.has(c.id) ? "Browser helper" : "CSV export";
  }
  return "Coming soon";
}

function formatStamp(iso) {
  if (!iso) return "—";
  const d = Date.parse(iso);
  if (!Number.isFinite(d)) return iso.slice(0, 10);
  return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

async function downloadHelperZip() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const apiBase = (typeof document !== "undefined" && document.documentElement.dataset.shoptosaApi) || "";
  const res = await api.get("/seller/extension/download", {
    responseType: "blob",
    params: { origin, api: apiBase },
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "shoptosa-helper.zip";
  a.click();
  URL.revokeObjectURL(url);
}

function helperIsInstalled() {
  return helperSeen() || Boolean(window.__SHOPTOSA_HELPER);
}

function HelperConnectWizard({ row, channel, handle, onHandle, open, busy, onClose, onAllow }) {
  const [step, setStep] = useState("consent");
  const [detected, setDetected] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const name = row?.name || channel;

  useEffect(() => {
    if (!open) return;
    setStep("consent");
    const tick = () => setDetected(helperIsInstalled());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  const openSite = () => {
    const ok = openHelperSite(channel);
    if (!ok) toast.error(`Allow pop-ups, then open ${name} to sign in or register.`);
  };

  const download = async () => {
    setDlBusy(true);
    try {
      await downloadHelperZip();
      toast.success("Downloaded shoptosa-helper.zip — Load unpacked in chrome://extensions");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose} data-testid="helper-connect-wizard">
      <div
        className="w-full max-w-lg bg-aura-surface border border-aura-border rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-4">
          {step === "helper" ? "Install helper" : "Authorize"}
        </div>
        {step === "helper" && (
          <>
            <h2 className="text-xl font-semibold mb-2">Install the listing helper</h2>
            <p className="text-sm text-aura-muted leading-relaxed">
              {name} has no public listing API. The Chrome/Edge helper is required to stay in sync: it reads listed/sold tiles from your closet tab, and copies your Shoptosa pack onto their sell form. You click List on {name}.
            </p>
            <ol className="text-sm text-aura-muted mt-4 space-y-2 list-decimal pl-5">
              <li>Download the zip and unzip it (you should see manifest.json).</li>
              <li>Open chrome://extensions, turn on Developer mode, click Load unpacked, pick that folder.</li>
              <li>Refresh this Shoptosa tab so the helper can sign in.</li>
            </ol>
            <div className={`mt-4 text-sm ${detected ? "text-emerald-400" : "text-amber-400"}`}>
              {detected ? "Helper detected on this tab." : "Helper not detected yet."}
            </div>
            <button type="button" disabled={dlBusy} onClick={download} className="mt-4 w-full py-2.5 bg-aura-accent text-white text-sm rounded-lg">
              {dlBusy ? "Downloading…" : "Download helper"}
            </button>
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setStep("consent")} className="px-4 py-2 text-sm text-aura-muted">Back</button>
              <button type="button" onClick={() => setStep("consent")} className="px-4 py-2 text-sm border border-aura-border rounded-lg">
                Continue →
              </button>
            </div>
          </>
        )}
        {step === "consent" && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white">
                <MarketLogo id={channel} />
              </span>
              <div>
                <h2 className="text-xl font-semibold">Shoptosa wants to access your {name} account</h2>
                <p className="text-xs text-aura-muted mt-1">{handle ? `@${handle}` : "Signed in on their site in your browser"}</p>
              </div>
            </div>
            <p className="text-sm text-aura-muted leading-relaxed">
              A {name} tab should already be open so you can register or sign in. Shoptosa does not receive your {name} password. Stay signed in there, then Allow here and install the listing helper.
            </p>
            <a
              href={helperOpenUrl(channel)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { e.preventDefault(); openSite(); }}
              className="mt-4 w-full py-2.5 border border-aura-border text-sm rounded-lg inline-flex items-center justify-center"
            >
              Open {name} to sign in or register
            </a>
            <button type="button" onClick={() => setStep("helper")} className="mt-2 w-full py-2 text-xs text-aura-cyan">
              {detected ? "Helper detected — continue" : "Need the listing helper?"}
            </button>
            <p className="text-sm font-medium mt-4 mb-2">Shoptosa will:</p>
            <ul className="text-sm text-aura-muted space-y-1.5 list-disc pl-5">
              <li>Copy title, description, and price from your Shoptosa listing onto {name}’s sell form</li>
              <li>Read listed/sold tiles from the {name} closet tab you have open and update Shoptosa</li>
              <li>Leave List / Submit to you on {name} — the helper does not auto-publish</li>
            </ul>
            <p className="text-xs text-aura-muted mt-3">
              Shoptosa does not receive your {name} password or cookies, and it does not call {name}’s private APIs. The helper must stay installed to stay in sync.
            </p>
            <label className="block mt-4 text-xs text-aura-muted">
              Username on {name} (optional)
              <input
                value={handle}
                onChange={(e) => onHandle(e.target.value)}
                placeholder="Username"
                className="mt-1 w-full px-3 py-2 text-sm border border-aura-border rounded-lg bg-transparent text-aura-ink"
              />
            </label>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-aura-muted">Cancel</button>
              <button
                type="button"
                disabled={busy}
                onClick={onAllow}
                data-testid="helper-connect-allow"
                className="px-5 py-2.5 bg-aura-accent text-white text-sm rounded-lg"
              >
                {busy ? "Connecting…" : "Allow"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConnectPicker({ open, channels, onClose }) {
  const navigate = useNavigate();
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const options = (channels || []).filter((c) => canConnect(c) && (!c.connected || c.last_error) && !COMING_SOON_CHANNELS.has(c.id));

  useEffect(() => {
    if (open) setPicked("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose} data-testid="connect-picker">
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-aura-surface border border-aura-border rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="text-xl font-semibold">Connect a marketplace</h2>
            <p className="text-sm text-aura-muted mt-1">Pick a shop. Official APIs open that marketplace so you can allow Shoptosa. Helper shops open the site, then need the listing helper to stay in sync.</p>
          </div>
          <button type="button" onClick={onClose} className="text-aura-muted text-sm">Close</button>
        </div>
        {options.length === 0 ? (
          <p className="text-sm text-aura-muted mt-6">Every available marketplace is already connected.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {options.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setPicked(c.id)}
                className={`text-left rounded-xl border p-3 transition-colors ${picked === c.id ? "border-aura-accent bg-aura-accent/10" : "border-aura-border hover:border-aura-accent"}`}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white mb-3">
                  <MarketLogo id={c.id} />
                </span>
                <div className="font-medium text-sm">{c.name}</div>
                <div className="text-[11px] text-aura-muted mt-0.5">{CHANNEL_TAGLINE[c.id] || connectorBadge(c)}</div>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-aura-muted">Cancel</button>
          <button
            type="button"
            disabled={!picked || busy}
            onClick={async () => {
              const c = options.find((x) => x.id === picked);
              if (!c) return;
              setBusy(true);
              try {
                await beginConnect(c, navigate);
                onClose();
              } finally {
                setBusy(false);
              }
            }}
            className="px-4 py-2 text-sm bg-aura-accent text-white rounded-lg disabled:opacity-40"
          >
            {busy ? "Opening…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

const ID_CHANNELS = ["amazon", "amazon_in", "walmart", "flipkart"];

function CatalogIdEditor({ channel, product, onSaved }) {
  const need = product.needs_catalog_id?.[channel] || "";
  const amazon = channel === "amazon" || channel === "amazon_in";
  const walmart = channel === "walmart";
  const flipkart = channel === "flipkart";
  const [asin, setAsin] = useState(product.asin || "");
  const [upc, setUpc] = useState(product.upc || product.gtin || "");
  const [fsn, setFsn] = useState(product.fsn || "");
  const [maker, setMaker] = useState(product.manufacturer || "");
  const [origin, setOrigin] = useState(product.country_of_origin || "");
  const [weight, setWeight] = useState(product.package_weight ?? "");
  const [weightUnit, setWeightUnit] = useState(product.package_weight_unit || (channel === "amazon_in" ? "grams" : "pounds"));
  const [batteries, setBatteries] = useState(product.battery_quantity ?? "");
  const suggestedHsn = channel === "amazon_in" ? suggestHsn(product.title, product.category) : null;
  const [hsn, setHsn] = useState(product.hsn || suggestedHsn?.code || "");
  const [saving, setSaving] = useState(false);
  const [finding, setFinding] = useState(false);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    setAsin(product.asin || "");
    setUpc(product.upc || product.gtin || "");
    setFsn(product.fsn || "");
    setMaker(product.manufacturer || "");
    setOrigin(product.country_of_origin || "");
    setWeight(product.package_weight ?? "");
    setWeightUnit(product.package_weight_unit || (channel === "amazon_in" ? "grams" : "pounds"));
    setBatteries(product.battery_quantity ?? "");
    setHsn(product.hsn || (channel === "amazon_in" ? suggestHsn(product.title, product.category)?.code : "") || "");
  }, [product.asin, product.upc, product.gtin, product.fsn, product.manufacturer, product.country_of_origin, product.package_weight, product.package_weight_unit, product.battery_quantity, product.hsn, product.title, product.category, product.id, channel]);

  if (!ID_CHANNELS.includes(channel)) return null;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        asin,
        upc,
        gtin: upc,
        fsn,
        sku: product.sku || "",
      };
      if (amazon) {
        body.manufacturer = maker;
        body.country_of_origin = origin;
        body.package_weight_unit = weightUnit;
        if (weight !== "" && weight != null) body.package_weight = Number(weight);
        if (batteries !== "" && batteries != null) body.battery_quantity = Number(batteries);
        if (channel === "amazon_in") body.hsn = hsn;
      }
      await api.patch(`/products/${product.id}/catalog-ids`, body);
      toast.success("Catalog ID saved");
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 space-y-1">
      {need ? (
        <div className="text-[11px] text-amber-400">{need} — paste it here, then Post.</div>
      ) : (
        <div className="text-[11px] text-aura-muted">
          {amazon && !product.asin && !(product.upc || product.gtin)
            ? "ASIN is optional. Post creates the page and Amazon assigns the ASIN."
            : ""}
          {amazon && product.asin ? `ASIN ${product.asin}` : ""}
          {(amazon || walmart) && (product.upc || product.gtin) ? ` · UPC ${product.upc || product.gtin}` : ""}
          {flipkart
            ? `${product.fsn ? `FSN ${product.fsn}. ` : ""}Post matches this SKU in your Flipkart account. A new product is filled in Seller Hub by the helper. After Flipkart assigns the FSN, Post syncs price and stock.`
            : ""}
        </div>
      )}
      {channel === "amazon_in" && (
        <p className="text-[11px] text-aura-muted max-w-xl">
          These boxes match Amazon’s form. Manufacturer blank uses your shop name. Country is IN. Package weight blank sends 500 grams. Number of batteries is 0 when the item has none.
          {" "}HSN is the field Amazon calls External Product Information.
          {suggestedHsn
            ? ` This title is a ${suggestedHsn.label}, so the box shows ${suggestedHsn.code}. Change it if your GST invoice uses another code, then click Save.`
            : " Silk saree is 5007, cotton saree is 5208, and a cotton bed sheet is 6302."}
        </p>
      )}
      <div className="flex flex-wrap gap-2 items-end">
        {amazon && (
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
            ASIN
            <input
              value={asin}
              onChange={(e) => setAsin(e.target.value)}
              placeholder="B0XXXXXXXX"
              className="block mt-0.5 w-32 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
            />
          </label>
        )}
        {(amazon || walmart) && (
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
            UPC / GTIN
            <input
              value={upc}
              onChange={(e) => setUpc(e.target.value)}
              placeholder="012345678905"
              className="block mt-0.5 w-36 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
            />
          </label>
        )}
        {amazon && (
          <>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Manufacturer
              <input
                value={maker}
                onChange={(e) => setMaker(e.target.value)}
                placeholder="Shop name"
                className="block mt-0.5 w-36 px-2 py-1 text-xs border border-aura-border rounded bg-transparent"
              />
            </label>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Country of origin
              <input
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder={channel === "amazon_in" ? "IN" : "US"}
                className="block mt-0.5 w-24 px-2 py-1 text-xs border border-aura-border rounded bg-transparent"
              />
            </label>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Package weight
              <input
                type="number"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={channel === "amazon_in" ? "500" : "1"}
                className="block mt-0.5 w-24 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
              />
            </label>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Weight unit
              <select
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
                className="block mt-0.5 w-28 px-2 py-1 text-xs border border-aura-border rounded bg-transparent"
              >
                <option value="grams">grams</option>
                <option value="kilograms">kilograms</option>
                <option value="pounds">pounds</option>
                <option value="ounces">ounces</option>
              </select>
            </label>
            {channel === "amazon_in" && (
              <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
                HSN code
                <input
                  value={hsn}
                  onChange={(e) => setHsn(e.target.value)}
                  placeholder={suggestedHsn?.code || "5007"}
                  className="block mt-0.5 w-28 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
                />
              </label>
            )}
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Number of batteries
              <input
                type="number"
                min="0"
                value={batteries}
                onChange={(e) => setBatteries(e.target.value)}
                placeholder="0"
                className="block mt-0.5 w-24 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
              />
            </label>
          </>
        )}
        {(amazon || walmart) && (
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="px-2 py-1 text-[11px] border border-aura-border rounded"
          >
            {amazon ? "Save" : "Save ID"}
          </button>
        )}
        {amazon && (
          <button
            type="button"
            disabled={finding}
            onClick={async () => {
              setFinding(true);
              try {
                const { data } = await api.post(`/seller/connections/${channel}/amazon/matches`, { product_id: product.id });
                const rows = data.matches || [];
                setMatches(rows);
                if (!rows.length) toast.message("No branded Amazon match for this title. Paste your product's ASIN, or the barcode on the item.");
              } catch (e) {
                toast.error(apiError(e));
              } finally {
                setFinding(false);
              }
            }}
            className="px-2 py-1 text-[11px] border border-aura-border rounded"
          >
            {finding ? "Searching…" : "Find on Amazon"}
          </button>
        )}
      </div>
      {matches.length > 0 && (
        <ul className="mt-2 space-y-1">
          {matches.map((row) => (
            <li key={row.asin} className="flex items-center gap-2 text-xs">
              {row.image ? <img src={row.image} alt="" className="w-8 h-10 object-cover rounded bg-aura-sand" /> : null}
              <span className="min-w-0 flex-1 truncate">{row.brand ? `${row.brand} · ` : ""}{row.title || row.asin}</span>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  setAsin(row.asin);
                  setSaving(true);
                  try {
                    await api.patch(`/products/${product.id}/catalog-ids`, {
                      asin: row.asin,
                      upc,
                      gtin: upc,
                      fsn,
                      sku: product.sku || "",
                    });
                    toast.success(`Mapped to ${row.asin}`);
                    setMatches([]);
                    onSaved?.();
                  } catch (e) {
                    toast.error(apiError(e));
                  } finally {
                    setSaving(false);
                  }
                }}
                className="shrink-0 px-2 py-1 border border-aura-accent text-aura-accent rounded"
              >
                Use {row.asin}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SALES_CHANNELS = ["ebay", "etsy", "depop", "amazon", "amazon_in", "walmart", "flipkart"];

const CHANNEL_COPY = {
  ebay: {
    connected: "eBay is live. Post publishes a fixed-price listing (inventory item + offer). Sync sales pulls paid eBay orders and drops quantity; with sale detection on, other listed channels are marked sold.",
    disconnected: "Click Connect to authorize Shoptosa on eBay. Admin must save the eBay app Client ID, secret, and RuName first. You also need Payment, Shipping, and Return policies in eBay Seller Hub.",
    sale: "Sync reads paid orders and whether the live listing ended, sold out, or was removed. With sale detection on, Shoptosa delists the item on other connected marketplaces. With it off, those channels are flagged Need delist elsewhere.",
    post: "Post creates a live eBay listing. Photos must be public HTTPS URLs. eBay Seller Hub needs Payment, Shipping, and Return policies.",
    saleName: "eBay",
  },
  etsy: {
    connected: "Etsy is live. Post creates a shop listing (shipping profile + processing profile + photos + active). If Post says this login has no shop, paste the numeric shop ID below (shop owner account) then Post again.",
    disconnected: "Click Connect to authorize Shoptosa on Etsy. Admin must save the Etsy API key (and secret) first, with redirect {API}/api/seller/connections/oauth/callback. The shop needs a shipping profile.",
    sale: "Sync reads paid receipts and whether the Etsy listing was deactivated, expired, sold out, or deleted. Etsy has no listing webhooks, so Shoptosa polls. With sale detection on, other connected marketplaces are delisted. With it off, they are flagged Need delist elsewhere.",
    post: "Post creates or updates one Etsy listing for this item. The shop needs a shipping profile, a processing profile, and a return policy. Reconnect after Admin adds the Etsy app so write scopes are granted.",
    saleName: "Etsy",
  },
  depop: {
    connected: "Depop is live. Post upserts a listing by SKU (photos + price + qty). Sync sales pulls Depop orders and drops quantity; with sale detection on, other listed channels are marked sold.",
    disconnected: "Click Connect to authorize Shoptosa on Depop after Admin saves the invite-only client ID and secret (developers@depop.com). Redirect URI must be {API}/api/seller/connections/oauth/callback. You can also paste a partner API key.",
    sale: "When Depop reports an order, Shoptosa drops quantity (and marks the Depop listing sold at zero). Other listed channels are marked sold so you can delist them.",
    post: "Post creates a live Depop listing by SKU. Photos must be public HTTPS URLs. Listings need a brand and at least $1 / £1.",
    saleName: "Depop",
  },
  amazon: {
    connected: "Amazon is live. Post creates a new page from the title, the same way Seller Central's blank form does, and Amazon assigns the ASIN. Paste an ASIN only to sell on a page that already exists, or a UPC if the item has a barcode. Sync sales pulls Amazon orders and drops quantity.",
    disconnected: "Click Connect to authorize Shoptosa in Seller Central. Admin must save Amazon LWA client ID, secret, and SP-API app ID first. Redirect URI must be {API}/api/seller/connections/oauth/callback.",
    sale: "When Amazon reports an order, Shoptosa drops quantity (and deletes the Amazon listing at zero). Other listed channels are marked sold so you can delist them.",
    post: "Leave ASIN and UPC empty, then Post. Amazon assigns the ASIN when it creates the page. Manufacturer, package weight, and number of batteries are on this row. Blank weight sends 1 pound and blank batteries send 0. Paste an ASIN only to sell on an existing page.",
    saleName: "Amazon",
  },
  amazon_in: {
    connected: "Amazon.in is live. Post creates a new page from the title, the same way Seller Central's blank form does, and Amazon assigns the ASIN. Paste an ASIN only to sell on a page that already exists, or an EAN if the item has a barcode. Listings use INR. Sync sales pulls Amazon.in orders and drops quantity.",
    disconnected: "Click Connect to authorize Shoptosa in Seller Central India. Admin must save Amazon LWA client ID, secret, and SP-API app ID first.",
    sale: "When Amazon.in reports an order, Shoptosa drops quantity (and deletes the listing at zero). Other listed channels are marked sold so you can delist them.",
    post: "Leave ASIN and UPC empty, then Post. Amazon assigns the ASIN when it creates the page. Enter the HSN code from your GST invoice. Manufacturer, package weight, and number of batteries are on this row. Blank weight sends 500 grams and blank batteries send 0. Paste an ASIN only to sell on an existing page. Listings use INR.",
    saleName: "Amazon.in",
  },
  walmart: {
    connected: "Shopify is live. Import catalog first so SKUs stay on the item. Post updates inventory and price when the SKU already exists. Sync sales pulls Shopify orders.",
    disconnected: "Enter your store (mystore.myshopify.com), then click Connect and approve Shoptosa. Admin must save the Dev Dashboard client ID and secret first. Those are app keys, not a store token.",
    sale: "When Shopify reports an order, Shoptosa drops quantity. Other listed channels are marked sold so you can delist them.",
    post: "If the SKU is already in Seller Center, Post updates qty/price. New items need a UPC/GTIN — not photos alone. Import catalog, or paste the barcode, then Post.",
    saleName: "Shopify",
  },
  flipkart: {
    connected: "Flipkart is live. Post matches this SKU in your Flipkart account and syncs price and stock. A brand-new product is added once in Seller Hub with the same SKU so Flipkart can assign the FSN. Sync sales pulls shipments and drops quantity.",
    disconnected: "Click Connect to authorize Shoptosa on Flipkart (third-party OAuth). Admin must save the third-party App ID and secret from Partner Dashboard → Manage API Access. Redirect URL must be HTTPS {API}/api/seller/connections/oauth/callback — not a self-access Seller Hub app.",
    sale: "When Flipkart reports a shipment, Shoptosa drops quantity (and inactivates the listing at zero). Other listed channels are marked sold so you can delist them.",
    post: "Post uses this SKU. You do not paste an FSN. Seller Hub needs a fulfillment location, and a new product must be added there once with this same SKU.",
    saleName: "Flipkart",
  },
  pinterest: {
    connected: "Pinterest is live. Post sends this item to the catalog. A photo is required.",
    disconnected: "Click Connect and allow Shoptosa on Pinterest. You do not paste a catalog token.",
  },
  backmarket: {
    connected: "Back Market is live. Post updates the SKU that already exists on Back Market. Delist sets the quantity to zero.",
    disconnected: "Click Connect. Shoptosa uses the Back Market account saved in Admin. You do not paste a token.",
  },
  goat: {
    connected: "GOAT is live. Post needs the Alias catalog ID and a US size. Delist removes the listing.",
    disconnected: "Click Connect. Shoptosa uses the Alias account saved in Admin. You do not paste a token.",
  },
  abebooks: {
    connected: "AbeBooks is live. Post adds the book. Brand is sent as the author. Delist removes it.",
    disconnected: "Click Connect. Shoptosa uses the AbeBooks account saved in Admin. You do not paste a PIN.",
  },
  wix: {
    connected: "Wix is live. Post sends the title, price, and photo, and the product link appears on the item. Posting again updates that same product.",
    disconnected: "Click Connect and approve Shoptosa on your Wix site. The site needs Wix Stores. Admin saves the Shoptosa Wix app first.",
  },
  square: {
    connected: "Square is live. Post adds the catalog item. Delist removes it.",
    disconnected: "Click Connect and approve Shoptosa on your Square account. You do not paste a token.",
  },
  squarespace: {
    connected: "Squarespace is live. Post creates the product on your store page. Delist removes it.",
    disconnected: "Click Connect and pick your Squarespace site. You do not paste an API key.",
  },
  google_shopping: {
    connected: "Google Shopping is live. Post sends the item to Merchant Center. A photo is required. Delist removes the offer.",
    disconnected: "Click Connect with the Google account that owns Merchant Center. You do not paste an account ID.",
  },
  discogs: {
    connected: "Discogs is live. Post needs the numeric release ID on the item. Delist removes the listing.",
    disconnected: "Click Connect and allow Shoptosa on your Discogs account. You do not paste a token.",
  },
  bonanza: {
    connected: "Bonanza is live. Post needs the Bonanza category ID on the item. Delist ends the listing.",
    disconnected: "Click Connect and log in to approve your Bonanza booth. You do not paste a user token.",
  },
  stockx: {
    connected: "StockX is live. Post needs the StockX variant ID on the item. Delist removes the ask.",
    disconnected: "Click Connect and allow Shoptosa on your StockX account. You do not paste an API key.",
  },
};

function ConnectionCards() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["seller-connections"],
    queryFn: async () => (await api.get("/seller/connections")).data,
    refetchInterval: 4000,
    placeholderData: (previous) => previous,
  });
  const channels = (data?.channels || []).filter((c) => !HIDDEN_SELLER_CHANNELS.has(c.id));
  const soonChannels = channels.filter((c) => COMING_SOON_CHANNELS.has(c.id) && !c.native);
  const soonSet = new Set(soonChannels.map((c) => c.id));
  const mainChannels = channels.filter((c) => !soonSet.has(c.id));
  const connector = data?.connector || {};
  const available = channels.filter((c) => canConnect(c) && (!c.connected || c.last_error)).length;

  useEffect(() => {
    const oauth = searchParams.get("oauth");
    if (oauth === "error" || oauth === "missing") {
      toast.error(searchParams.get("reason") || "Marketplace connection failed. Click Connect and approve the shop again.");
      const next = new URLSearchParams(searchParams);
      next.delete("oauth");
      next.delete("reason");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-connections">
      <PageIntro
        kicker="Marketplaces"
        title="Marketplaces"
        blurb="Every shop this account is linked to. Click a card to manage defaults, sale detection, and sync. Official APIs import catalog and post; helper shops copy a pack and you click List on their site."
        actions={
          <>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="connect-marketplace"
              className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Connect a marketplace
            </button>
            <Link to="/seller/add" className="px-4 py-2.5 border border-aura-border text-sm rounded-lg">Create item</Link>
          </>
        }
      />
      <div className="mb-4 px-4 py-3 rounded-lg border border-aura-border text-sm text-aura-muted">
        {connector.sandbox
          ? "Sandbox is on. Covered channels connect locally so you can try the flow. Register free eBay/Etsy/Amazon apps or a Depop partner app in Admin for live OAuth, or paste seller API tokens on the channel page."
          : connector.native
            ? "Shoptosa connector is live. Connect uses official marketplace APIs — OAuth when an app is registered, otherwise the seller’s own API token."
            : "Connect uses each marketplace’s official API from this dashboard."}
      </div>
      {isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 shimmer rounded-lg" />)}</div>
      ) : isError && !data?.channels?.length ? (
        <p className="text-sm text-aura-muted">Marketplaces did not load. Reload this page.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {mainChannels.map((c) => {
            const kind = statusKind(c);
            const startNow = canConnect(c) && kind !== "connected" && !c.native;
            const body = (
              <>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shrink-0">
                      {c.id === "shoptosa" ? <span className="text-xs font-semibold text-black">S</span> : <MarketLogo id={c.id} />}
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-aura-muted truncate">
                        {accountLabel(c) || connectorBadge(c)}
                      </div>
                    </div>
                  </div>
                  <StatusPill c={c} />
                </div>
                {kind === "connected" || c.native ? (
                  <>
                    <div className="text-[10px] tracking-luxe uppercase text-aura-muted" title="Shoptosa items with status Listed on this marketplace. This is not a live count from the marketplace.">Listed in Shoptosa</div>
                    <div className="text-2xl font-mono mt-1">{c.listed}</div>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs">
                      <span className="text-aura-muted">View in inventory</span>
                      <span className="text-aura-accent">Manage →</span>
                    </div>
                  </>
                ) : kind === "error" ? (
                  <>
                    <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      {c.last_error || `Shoptosa can't sync to ${c.name} until you reconnect.`}
                    </div>
                    <div className="text-xs text-red-400 mt-auto pt-4">Reconnect →</div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-aura-muted leading-relaxed">
                      Not connected. Connect opens {c.name} so you can register or authorize this shop.
                    </p>
                    <div className="text-xs text-aura-accent mt-auto pt-4">
                      {canConnect(c) ? `Connect ${c.name} →` : "View form →"}
                    </div>
                  </>
                )}
              </>
            );
            const cls = "bg-aura-surface border border-aura-border rounded-xl p-5 hover:border-aura-accent transition-colors flex flex-col text-left w-full";
            if (startNow) {
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`connection-card-${c.id}`}
                  className={cls}
                  onClick={() => beginConnect(c, navigate)}
                >
                  {body}
                </button>
              );
            }
            return (
              <Link
                key={c.id}
                to={`/seller/connections/${c.id}`}
                data-testid={`connection-card-${c.id}`}
                className={cls}
              >
                {body}
              </Link>
            );
          })}
        </div>
      )}
      {soonChannels.length > 0 && (
        <div className="mt-10">
          <h2 className="text-[11px] tracking-luxe uppercase text-aura-muted mb-4">Connect more marketplaces coming soon</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {soonChannels.map((c) => (
              <div key={c.id} className="bg-aura-surface border border-aura-border rounded-xl p-5 flex items-center gap-3" data-testid={`connection-soon-${c.id}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shrink-0">
                  <MarketLogo id={c.id} />
                </span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-aura-muted">Coming soon</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-10 bg-aura-surface border border-aura-border rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-medium">Listing helper</div>
          <p className="text-sm text-aura-muted mt-1 max-w-xl">
            Required to copy packs onto Poshmark, Mercari, Grailed, Facebook Marketplace, Meesho, Myntra, AJIO, Nykaa, Tata CLiQ, and OLX. You stay signed in and click List yourself.
          </p>
        </div>
        <Link to="/seller/extension" className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg">
          Install helper
        </Link>
      </div>
      {available > 0 && (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="mt-4 w-full border border-dashed border-aura-border rounded-xl p-5 text-sm text-aura-muted hover:border-aura-accent hover:text-aura-ink"
        >
          Connect a marketplace · {available} more available
        </button>
      )}
      <ConnectPicker open={pickerOpen} channels={channels} onClose={() => setPickerOpen(false)} />
    </div>
  );
}

function ConnectionDetail() {
  const { channel } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const { user, updateProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cred, setCred] = useState({});
  const [credFields, setCredFields] = useState(null);
  const [shopId, setShopId] = useState("");
  const [originPostal, setOriginPostal] = useState("");
  const [originCountry, setOriginCountry] = useState("US");
  const [helperHandle, setHelperHandle] = useState("");
  const [shopifyShop, setShopifyShop] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [acceptsReturns, setAcceptsReturns] = useState(true);
  const [returnDays, setReturnDays] = useState(30);
  const salesOnce = useRef("");
  const startOnce = useRef("");
  const { data, isLoading } = useQuery({
    queryKey: ["seller-connection", channel],
    queryFn: async () => (await api.get(`/seller/connections/${channel}`)).data,
    refetchInterval: 4000,
  });
  const { data: returnPolicies, isError: returnPoliciesError, error: returnPoliciesErr, isLoading: returnPoliciesLoading } = useQuery({
    queryKey: ["etsy-return-policies", channel],
    enabled: channel === "etsy" && Boolean(data?.channel?.connected),
    queryFn: async () => (await api.get("/seller/connections/etsy/return-policies")).data,
    retry: false,
  });
  const row = data?.channel;
  const items = data?.items || [];
  const unpublished = data?.unpublished || [];
  const plan = data?.plan || {};

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.data?.type !== "SHOPTOSA_HELPER_LISTED") return;
      if (ev.data.channel && ev.data.channel !== channel) return;
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
      if (ev.data.product_id) {
        toast.success("Your item is listed");
        navigate(`/seller/crosslist/${ev.data.product_id}?channel=${channel === "walmart" ? "shopify" : channel}`);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [channel, navigate, qc]);
  const connector = data?.connector || {};

  useEffect(() => {
    const oauth = searchParams.get("oauth");
    if (oauth === "error" || oauth === "missing") {
      toast.error("Wix approval did not save. Click Connect again.");
      const next = new URLSearchParams(searchParams);
      next.delete("oauth");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("connected") !== "1") return;
    toast.success("Connected through Shoptosa");
    const next = new URLSearchParams(searchParams);
    next.delete("connected");
    setSearchParams(next, { replace: true });
    if (plan.via === "unified") {
      api.post(`/seller/connections/${channel}/unified/sync`).then((res) => {
        qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
        qc.invalidateQueries({ queryKey: ["seller-connections"] });
        const bits = [];
        if (res.data?.imported) bits.push(`Imported ${res.data.imported}`);
        if (res.data?.updated) bits.push(`updated ${res.data.updated}`);
        if (bits.length) toast.success(bits.join(", "));
      }).catch(() => {});
    }
  }, [searchParams, setSearchParams, plan.via, channel, qc]);

  useEffect(() => {
    setShopId(row?.unified?.shop_id || "");
    if (row?.unified?.shop_id) setShopifyShop(row.unified.shop_id);
  }, [row?.unified?.shop_id]);

  useEffect(() => {
    setHelperHandle(row?.handle || "");
  }, [row?.handle]);

  const profile = user?.profile;
  useEffect(() => {
    const p = profile || {};
    setOriginPostal(p.address_postal || "");
    setOriginCountry(p.address_country || p.country || "US");
  }, [profile]);

  useEffect(() => {
    if (!SALES_CHANNELS.includes(channel) || !row?.connected || row?.unified?.sandbox || salesOnce.current === channel) return;
    salesOnce.current = channel;
    api.post(`/seller/connections/${channel}/unified/sales`).then((res) => {
      if (res.data?.updated) {
        qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
        qc.invalidateQueries({ queryKey: ["seller-connections"] });
        const name = CHANNEL_COPY[channel]?.saleName || channel;
        toast.success(`Synced ${res.data.updated} ${name} sale${res.data.updated === 1 ? "" : "s"}`);
      }
    }).catch(() => {});
  }, [channel, row?.connected, row?.unified?.sandbox, qc]);

  const finishUnified = async (channelId) => {
    qc.invalidateQueries({ queryKey: ["seller-connection", channelId] });
    qc.invalidateQueries({ queryKey: ["seller-connections"] });
    toast.success(`${row?.name || channelId} connected through ${plan.label || "the unified API"}`);
  };

  const connectUnified = async () => {
    setBusy(true);
    try {
      const asksShop = channel === "walmart" || channel === "woocommerce";
      const started = (await api.post(`/seller/connections/${channel}/unified/start`, asksShop ? { shop: shopifyShop.trim() } : {})).data;
      if (started.flow === "shop") {
        toast.error(started.message || "Enter your Shopify store, like mystore.myshopify.com");
        return;
      }
      if (started.flow === "sandbox") {
        await api.post(`/seller/connections/${channel}/unified/sandbox`, { provider: started.provider || "" });
        await finishUnified(channel);
        return;
      }
      if (started.flow === "platform") {
        await api.post(`/seller/connections/${channel}/unified/credentials`, {});
        await finishUnified(channel);
        return;
      }
      if (started.flow === "oauth" && started.auth_url) {
        toast.message(`Opening ${row?.name || channel} so you can allow Shoptosa.`);
        window.location.href = started.auth_url;
        return;
      }
      if (started.flow === "credentials") {
        setCredFields(started.fields || []);
        toast.message("Paste this shop's API credentials, then Save connection.");
        return;
      }
      toast.error(started.message || "This marketplace isn’t available to connect from the dashboard yet.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitCredentials = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/connections/${channel}/unified/credentials`, cred);
      setCredFields(null);
      await finishUnified(channel);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveShopId = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (shopId.trim()) {
        await api.post(`/seller/connections/${channel}/unified/shop-id`, { shop_id: shopId });
      }
      if (originPostal.trim()) {
        await updateProfile({
          address_postal: originPostal.trim(),
          address_country: (originCountry || "US").toUpperCase().slice(0, 2),
        });
      }
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      toast.success("Etsy shipping details saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const connectHelper = async () => {
    setBusy(true);
    try {
      await api.post("/seller/extension/connect", {
        channel,
        handle: helperHandle,
        host: HELPER_HOST[channel] || "",
      });
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      setWizardOpen(false);
      toast.success(`${row?.name || channel} connected. Keep the helper installed, then click List on their site.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    if (plan.via === "helper" && HELPER_BROWSER.has(channel)) {
      openHelperSite(channel);
      setWizardOpen(true);
      return;
    }
    if (plan.via !== "unified") {
      toast.error("This marketplace isn’t available to connect from the dashboard yet.");
      return;
    }
    await connectUnified();
  };

  useEffect(() => {
    if (searchParams.get("start") !== "1" || isLoading || !row || startOnce.current === channel) return;
    startOnce.current = channel;
    const next = new URLSearchParams(searchParams);
    next.delete("start");
    setSearchParams(next, { replace: true });
    if (row.native) return;
    if (plan.via === "helper" && HELPER_BROWSER.has(channel)) {
      setWizardOpen(true);
      return;
    }
    if (plan.via === "unified") connectUnified();
  }, [searchParams, isLoading, row, channel, plan.via, setSearchParams]);

  const syncSales = async () => {
    setBusy(true);
    try {
      const res = (await api.post(`/seller/connections/${channel}/unified/sales`)).data;
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      toast.success(res.summary || (res.applied ? `Applied ${res.applied} sale${res.applied === 1 ? "" : "s"}` : (res.note || "No listing changes")));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const syncCatalog = async () => {
    setBusy(true);
    try {
      const res = (await api.post(`/seller/connections/${channel}/unified/sync`)).data;
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      const bits = [];
      if (res.imported) bits.push(`Imported ${res.imported} listing${res.imported === 1 ? "" : "s"}`);
      if (res.updated) bits.push(`updated ${res.updated} with catalog IDs`);
      toast.success(bits.join(", ") || "Catalog is already in sync");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const helperSync = () => {
    if (!helperSeen()) {
      const site = typeof window !== "undefined" ? window.location.host : "this site";
      toast.error(`Helper not visible on this tab. Remove any old "Shoptosa listing helper", re-download the helper here, Load unpacked, then hard-refresh (Ctrl+Shift+R) so it attaches to ${site}.`);
      return;
    }
    setBusy(true);
    let finished = false;
    const done = (ev) => {
      if (ev.data?.type !== "SHOPTOSA_HELPER_SYNC_RESULT") return;
      finished = true;
      window.removeEventListener("message", done);
      setBusy(false);
      if (ev.data.error) {
        toast.error(ev.data.error);
        return;
      }
      const bits = [];
      if (ev.data.seen) bits.push(`saw ${ev.data.seen} on ${row?.name || channel}`);
      if (ev.data.matched) bits.push(`updated ${ev.data.matched}`);
      if (ev.data.incomplete) bits.push(ev.data.ended ? "incomplete — listings not in this scan were left unchanged" : "incomplete — no endings applied");
      else if (ev.data.ended) bits.push(`ended ${ev.data.ended}`);
      else if (ev.data.sold) bits.push(`${ev.data.sold} sold`);
      if (ev.data.helper_delists?.length) bits.push(`delisting ${ev.data.helper_delists.length} other marketplace${ev.data.helper_delists.length === 1 ? "" : "s"}`);
      if (ev.data.imported) bits.push(`imported ${ev.data.imported}`);
      toast.success(bits.join(" · ") || "Sync finished");
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    };
    window.addEventListener("message", done);
    window.postMessage({ type: "SHOPTOSA_HELPER_SYNC", channel, handle: helperHandle }, "*");
    setTimeout(() => {
      if (finished) return;
      window.removeEventListener("message", done);
      setBusy(false);
      toast.error("Helper did not respond. Reload this Shoptosa tab after loading the listing helper, and keep your closet tab open.");
    }, 45000);
  };

  const markHelperListed = async (productId, status) => {
    setBusy(true);
    try {
      await api.put(`/seller/crosslist/${productId}`, { channels: { [channel]: { status } } });
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      toast.success(status === "listed" ? "Marked listed" : "Marked not listed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const publishItem = async (productId) => {
    setBusy(true);
    try {
      const res = (await api.post(`/seller/connections/${channel}/unified/publish`, { product_ids: [productId] })).data;
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      toast.success(res.posted ? `Posted ${res.posted} listing${res.posted === 1 ? "" : "s"}` : "Nothing posted");
      const url = res.listings?.[0]?.listing_url;
      if (url) toast.message(url);
      if (res.errors?.length) toast.error(res.errors[0].error);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const delistItem = async (productId) => {
    setBusy(true);
    try {
      const res = (await api.post(`/seller/connections/${channel}/unified/delist`, { product_ids: [productId] })).data;
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
      toast.success(res.delisted ? `Delisted ${res.delisted}` : "Nothing delisted");
      if (res.errors?.length) toast.error(res.errors[0].error);
    } catch (e) {
      toast.error(apiError(e));
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/connections/${channel}/disconnect`);
      toast.success("Disconnected");
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      qc.invalidateQueries({ queryKey: ["seller-connections"] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleSale = async () => {
    if (!row || row.native) return;
    setBusy(true);
    try {
      await api.put(`/seller/connections/${channel}/sale-detection`, { enabled: !row.sale_detection });
      qc.invalidateQueries({ queryKey: ["seller-connection", channel] });
      toast.success(row.sale_detection
        ? "Sale detection off. Shoptosa still updates this channel and flags other listings that need a manual delist."
        : "Sale detection on. A sale or ended listing here delists the item on other connected marketplaces.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading || !row) {
    return <div className="max-w-[900px] mx-auto px-6 pt-28 text-aura-muted">Loading…</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="connection-detail">
      <button type="button" onClick={() => navigate("/seller/connections")} className="text-xs text-aura-muted mb-6">← Marketplaces</button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-white">
            {row.native ? <span className="text-sm font-semibold text-black">S</span> : <MarketLogo id={row.id} />}
          </span>
          <div>
            <h1 className="text-3xl font-semibold">{row.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusPill c={row} />
              {accountLabel(row) && accountLabel(row) !== "Connected" ? <span className="text-sm text-aura-muted">{accountLabel(row)}</span> : row.connected && !row.native ? <span className="text-sm text-aura-muted">Connected</span> : null}
              {!row.native ? <span className="text-xs text-aura-muted">{connectorBadge(row)}</span> : null}
            </div>
          </div>
        </div>
        {!row.native && plan.via === "helper" && HELPER_BROWSER.has(row.id) && (
          <div className="flex flex-wrap gap-2 items-center">
            <input
              value={helperHandle}
              onChange={(e) => setHelperHandle(e.target.value)}
              placeholder="Username (optional)"
              className="w-40 px-3 py-2 text-sm border border-aura-border rounded-lg bg-transparent"
            />
            <button type="button" disabled={busy} onClick={connect} className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg">
              {row.connected ? "Reconnect" : "Connect"}
            </button>
            {row.connected && (
              <button type="button" disabled={busy} onClick={disconnect} className="px-4 py-2.5 bg-red-600 text-white text-sm rounded-lg">
                Disconnect
              </button>
            )}
            <Link to="/seller/extension" className="px-4 py-2.5 border border-aura-border text-sm rounded-lg">
              Download helper
            </Link>
          </div>
        )}
        {!row.native && plan.via === "unified" && (
          <div className="flex flex-wrap gap-2 items-center">
            {(channel === "walmart" || channel === "woocommerce") && (
              <input
                value={shopifyShop}
                onChange={(e) => setShopifyShop(e.target.value)}
                placeholder={channel === "woocommerce" ? "https://your-store.com" : "store.myshopify.com"}
                className="w-64 px-3 py-2 text-sm border border-aura-border rounded-lg bg-transparent"
              />
            )}
            <button type="button" disabled={busy} onClick={connect} className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg">
              {row.connected ? "Reconnect" : "Connect"}
            </button>
            {row.connected && (
              <button type="button" disabled={busy} onClick={disconnect} className="px-4 py-2.5 bg-red-600 text-white text-sm rounded-lg">
                Disconnect
              </button>
            )}
          </div>
        )}
      </div>

      {row.last_error ? (
        <div className="mb-6 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          {row.last_error}{channel === "wix" ? "" : " Reconnect, then Sync sales or Import catalog."}
        </div>
      ) : null}

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Total active items</div>
          <div className="text-2xl font-mono mt-2">{row.listed}</div>
          <Link to={`/seller/crosslist?channel=${row.id}`} className="text-xs text-aura-cyan mt-2 inline-block">View in inventory</Link>
        </div>
        <div className="bg-aura-surface border border-aura-border rounded-lg p-5">
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Last sync</div>
          <div className="text-lg font-mono mt-2">{formatStamp(row.last_sync_at || row.connected_at)}</div>
          {row.connected && plan.via === "unified" && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" disabled={busy} onClick={syncCatalog} className="px-3 py-1.5 border border-aura-border text-xs rounded-lg">
                Import catalog
              </button>
              {SALES_CHANNELS.includes(channel) && (
                <button type="button" disabled={busy} onClick={syncSales} className="px-3 py-1.5 border border-aura-border text-xs rounded-lg">
                  Sync sales
                </button>
              )}
            </div>
          )}
          {row.connected && plan.via === "helper" && HELPER_BROWSER.has(channel) && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" disabled={busy} onClick={helperSync} data-testid="helper-sync" className="px-3 py-1.5 border border-aura-border text-xs rounded-lg">
                Sync {row.name}
              </button>
              <p className="w-full text-[11px] text-aura-muted mt-1">
                Keep your {row.name} closet tab open and signed in. Sync reads listed/sold tiles from that page.
              </p>
              <button type="button" onClick={() => openHelperSite(channel, { sell: true })} className="px-3 py-1.5 border border-aura-border text-xs rounded-lg">
                Open {row.name} sell page
              </button>
              <Link to="/seller/extension" className="px-3 py-1.5 border border-aura-border text-xs rounded-lg">
                Listing helper
              </Link>
            </div>
          )}
        </div>
      </div>

      {!row.native && plan.via === "unified" && (
        <div className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-8 text-sm text-aura-muted">
          {row.connected
            ? (CHANNEL_COPY[channel]?.connected || `This channel uses the Shoptosa connector — official ${row.name} APIs, no aggregator. Import catalog pulls products through that API.`)
            : (CHANNEL_COPY[channel]?.disconnected || `Connect uses ${row.name}’s official API (OAuth if a Shoptosa app is registered, otherwise your seller token).`)}
          {connector.sandbox ? " Sandbox is on until you add marketplace app keys in Admin or paste a live token." : ""}
        </div>
      )}
      {channel === "etsy" && row.connected && plan.via === "unified" && (
        <form
          onSubmit={saveShopId}
          className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-8 space-y-2"
        >
          <div className="font-medium">Etsy shop & ship-from</div>
          <p className="text-xs text-aura-muted">
            Etsy needs a ship-from postal code to create a shipping profile. Paste your ZIP below (same as Shop Manager). Shop ID is only needed if Connect used a login that is not the shop owner.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Ship-from ZIP / postal
              <input
                value={originPostal}
                onChange={(e) => setOriginPostal(e.target.value)}
                placeholder="10001"
                className="block mt-0.5 w-32 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
              />
            </label>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Country
              <input
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
                placeholder="US"
                maxLength={2}
                className="block mt-0.5 w-16 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent uppercase"
              />
            </label>
            <label className="text-[10px] tracking-luxe uppercase text-aura-muted">
              Shop ID
              <input
                value={shopId}
                onChange={(e) => setShopId(e.target.value)}
                placeholder="12345678"
                className="block mt-0.5 w-40 px-2 py-1 text-xs font-mono border border-aura-border rounded bg-transparent"
              />
            </label>
            <button type="submit" disabled={busy} className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg">
              Save
            </button>
          </div>
        </form>
      )}
      {!row.native && plan.via !== "unified" && (
        <div className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-8 text-sm text-aura-muted space-y-2">
          {row.connected && plan.via === "helper" && HELPER_BROWSER.has(row.id) ? (
            <>
              <p>
                The offering on {row.name}: Shoptosa cannot publish for you. List on {row.name} opens the Shoptosa listing form for that item. Click List on {row.name} there to type title, description, and price on their create form. You add photos if needed.
              </p>
              <p>
                Sync reads listed/sold tiles from the closet tab you have open (no cookies sent to Shoptosa). After you click List, Sync or Mark listed so Active items stay honest.
              </p>
            </>
          ) : (
            <p>{plan.partner?.blurb || `${row.name} has no public listing API. Download the listing helper to copy your Shoptosa pack while you click List on their site.`}</p>
          )}
          {plan.via === "helper" && HELPER_BROWSER.has(row.id) && !row.connected && (
            <p>
              Click Connect. That opens {row.name} so you can register or sign in, then asks you to Allow Shoptosa and install the listing helper.
            </p>
          )}
          {plan.partner?.url && (
            <a href={plan.partner.url} target="_blank" rel="noreferrer" className="text-aura-cyan block">
              {plan.partner.url_label || plan.partner.url}
            </a>
          )}
          {plan.via === "helper" && HELPER_BROWSER.has(row.id) && (
            <Link to="/seller/extension" className="text-aura-cyan block">Download listing helper →</Link>
          )}
          <Link to="/seller/crosslist" className="text-aura-cyan block">Inventory & item forms →</Link>
        </div>
      )}
      {credFields && (
        <form
          className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-8 space-y-3"
          onSubmit={(e) => { e.preventDefault(); submitCredentials(); }}
        >
          <div className="font-medium">{row.name} API credentials</div>
          <p className="text-xs text-aura-muted">Stored on your Shoptosa seller account. Tokens stay in your shop — they are not sent to a third-party aggregator.</p>
          {credFields.map((f) => (
            <label key={f.id} className="block text-sm">
              <span className="text-aura-muted text-xs">{f.label}</span>
              <input
                className="mt-1 w-full bg-transparent border border-aura-border rounded-lg px-3 py-2"
                placeholder={f.placeholder || ""}
                value={cred[f.id] || ""}
                onChange={(e) => setCred((prev) => ({ ...prev, [f.id]: e.target.value }))}
              />
            </label>
          ))}
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg">Save connection</button>
            <button type="button" onClick={() => setCredFields(null)} className="px-4 py-2 text-sm text-aura-muted">Cancel</button>
          </div>
        </form>
      )}

      {channel === "etsy" && row.connected && (
        <div className="mb-8 bg-aura-surface border border-aura-border rounded-xl p-5" data-testid="etsy-return-policy">
          <h2 className="font-medium mb-1">Return policy</h2>
          <p className="text-sm text-aura-muted mb-3">
            Etsy will not activate a listing when the shop has no return policy. Shoptosa checks this before posting and does not send a null policy id.
          </p>
          {returnPoliciesLoading ? (
            <p className="text-sm text-aura-muted mb-3">Loading return policies…</p>
          ) : returnPoliciesError ? (
            <p className="text-sm text-amber-200 mb-3">{apiError(returnPoliciesErr)} A policy already saved on this connection is still used when you Post.</p>
          ) : (returnPolicies?.policies || []).length ? (
            <label className="block text-sm mb-3">
              <span className="text-aura-muted text-xs">Use this policy</span>
              <select
                className="mt-1 w-full bg-transparent border border-aura-border rounded-lg px-3 py-2"
                value={returnPolicies.selected || returnPolicies.policies[0].return_policy_id}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.post("/seller/connections/etsy/return-policy", { return_policy_id: e.target.value });
                    qc.invalidateQueries({ queryKey: ["etsy-return-policies", channel] });
                    toast.success("Return policy saved");
                  } catch (err) {
                    toast.error(apiError(err));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {returnPolicies.policies.map((policy) => (
                  <option key={policy.return_policy_id} value={policy.return_policy_id}>
                    {policy.accepts_returns ? `Returns within ${policy.return_deadline || "?"} days` : "No returns"} · {policy.return_policy_id}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-sm text-amber-200 mb-3">This shop has no return policy yet. Create one here or in Shop Manager, then Post.</p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="text-aura-muted text-xs">Accepts returns</span>
              <select className="mt-1 block bg-transparent border border-aura-border rounded-lg px-3 py-2" value={acceptsReturns ? "yes" : "no"} onChange={(e) => setAcceptsReturns(e.target.value === "yes")}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            {acceptsReturns ? (
              <label className="text-sm">
                <span className="text-aura-muted text-xs">Return window</span>
                <select className="mt-1 block bg-transparent border border-aura-border rounded-lg px-3 py-2" value={returnDays} onChange={(e) => setReturnDays(Number(e.target.value))}>
                  {[7, 14, 21, 30, 45, 60, 90].map((days) => <option key={days} value={days}>{days} days</option>)}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post("/seller/connections/etsy/return-policy", {
                    accepts_returns: acceptsReturns,
                    accepts_exchanges: acceptsReturns,
                    return_deadline: returnDays,
                  });
                  qc.invalidateQueries({ queryKey: ["etsy-return-policies", channel] });
                  toast.success("Etsy return policy created");
                } catch (err) {
                  toast.error(apiError(err));
                } finally {
                  setBusy(false);
                }
              }}
              className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg"
            >
              Create return policy
            </button>
          </div>
        </div>
      )}

      {!row.native && (plan.via === "unified" || (plan.via === "helper" && HELPER_BROWSER.has(row.id))) && (
        <div className="mb-8">
          <h2 className="font-medium mb-1">Settings</h2>
          <p className="text-sm text-aura-muted mb-3">How {row.name} behaves in this shop — sale detection across connected channels.</p>
          <div className="bg-aura-surface border border-aura-border rounded-xl p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">Sale detection</div>
                <p className="text-sm text-aura-muted mt-1">
                  {plan.via === "helper"
                    ? `Sync via the listing helper reads sold tiles from your ${row.name} closet. When sale detection is on, a sold listing flags other listed channels.`
                    : SALES_CHANNELS.includes(channel)
                      ? CHANNEL_COPY[channel].sale
                      : `When a listing sells on ${row.name}, flag it as sold on every other listed channel so you can delist there.`}
                </p>
                <div className="text-xs text-aura-muted mt-2">
                  {row.sale_detection
                    ? "On — ending or selling here delists other connected marketplaces"
                    : "Off — this channel still updates when the listing ends. Other channels are flagged Need delist elsewhere."}
                </div>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={toggleSale}
                data-testid="sale-detection-toggle"
                className={`w-12 h-7 rounded-full ${row.sale_detection ? "bg-aura-accent" : "bg-aura-border"}`}
              >
                <span className={`block w-5 h-5 bg-white rounded-full transition-transform ${row.sale_detection ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-medium mb-3">Active items</h2>
      {items.length === 0 ? (
        <div className="text-sm text-aura-muted">
          {plan.via === "helper" ? "None yet on this shop. Fill a sell form below, click List on their site, then Sync or Mark listed." : "None yet. Import catalog or post from inventory below."}
        </div>
      ) : (
        <div className="divide-y divide-aura-border border border-aura-border rounded-lg mb-8">
          {items.map((p) => {
            const st = p.channels?.[channel]?.status || "off";
            return (
              <div key={p.id} className="flex items-center gap-3 p-3">
                <Link to={`/seller/crosslist/${p.id}?channel=${channel === "walmart" ? "shopify" : channel}`} className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-90">
                  <img src={imgUrl(p.images?.[0])} alt="" className="w-12 h-14 object-cover rounded bg-aura-sand" />
                  <div className="min-w-0">
                    <div className="truncate text-sm">{p.title}</div>
                    <div className="text-xs text-aura-muted">{st === "ready" ? "Ready to mark listed · " : ""}{p.stale ? "Stale · " : ""}{p.quantity} in stock</div>
                  </div>
                </Link>
                {plan.via === "helper" && HELPER_BROWSER.has(channel) && (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {st === "ready" && (
                      <button type="button" disabled={busy} onClick={() => markHelperListed(p.id, "listed")} className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg">
                        Mark listed
                      </button>
                    )}
                    {st === "listed" && (
                      <>
                        {p.channels?.[channel]?.listing_url ? (
                          <a
                            href={p.channels[channel].listing_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg"
                          >
                            View listing
                          </a>
                        ) : null}
                        <button type="button" disabled={busy} onClick={() => markHelperListed(p.id, "off")} className="px-3 py-1.5 text-xs border border-aura-border rounded-lg">
                          Mark not listed
                        </button>
                      </>
                    )}
                  </div>
                )}
                {plan.via === "unified" && st === "listed" && (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {p.channels?.[channel]?.listing_url ? (
                      <a
                        href={p.channels[channel].listing_url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg"
                      >
                        View listing
                      </a>
                    ) : null}
                    <button type="button" disabled={busy} onClick={() => delistItem(p.id)} className="px-3 py-1.5 text-xs border border-aura-border rounded-lg">
                      Delist
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!row.native && unpublished.length > 0 && (
        <div className="mt-8">
          <h2 className="font-medium mb-3">Inventory not on {row.name}</h2>
          <p className="text-xs text-aura-muted mb-3">
            {plan.via === "unified"
              ? (CHANNEL_COPY[channel]?.post || "Post sends this item through the Shoptosa connector. Sandbox marks it listed locally.")
              : plan.via === "helper" && HELPER_BROWSER.has(row.id)
                ? `List on ${row.name} opens the Shoptosa listing form for that item. Click List on ${row.name} there after you review the pack — this page will not open a new ${row.name} tab.`
                : "This marketplace isn’t available to list from the dashboard yet. Export CSV from Crosslist."}
          </p>
          <div className="divide-y divide-aura-border border border-aura-border rounded-lg">
            {unpublished.map((p) => (
              <div key={p.id} className="flex items-start gap-3 p-3">
                <img src={imgUrl(p.images?.[0])} alt="" className="w-12 h-14 object-cover rounded bg-aura-sand" />
                <div className="min-w-0 flex-1">
                  <Link to={`/seller/crosslist/${p.id}?channel=${channel === "walmart" ? "shopify" : channel}`} className="truncate text-sm block">{p.title}</Link>
                  <div className="text-xs text-aura-muted">{p.quantity} in stock{p.sku ? ` · SKU ${p.sku}` : ""}</div>
                  <CatalogIdEditor
                    channel={channel}
                    product={p}
                    onSaved={() => qc.invalidateQueries({ queryKey: ["seller-connection", channel] })}
                  />
                </div>
                {row.connected && plan.via === "unified" && (
                  <button
                    type="button"
                    disabled={busy || !!p.needs_catalog_id?.[channel]}
                    title={p.needs_catalog_id?.[channel] || "Post this item"}
                    onClick={() => publishItem(p.id)}
                    className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg disabled:opacity-40 shrink-0 mt-1"
                  >
                    Post
                  </button>
                )}
                {row.connected && plan.via === "helper" && HELPER_BROWSER.has(row.id) && (
                  <div className="flex flex-col gap-1 shrink-0 mt-1">
                    <Link
                      to={`/seller/crosslist/${p.id}?channel=${channel === "walmart" ? "shopify" : channel}`}
                      data-testid={`helper-post-${p.id}`}
                      className="px-3 py-1.5 text-xs bg-aura-accent text-white rounded-lg text-center"
                    >
                      List on {row.name}
                    </Link>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => markHelperListed(p.id, "listed")}
                      className="px-3 py-1.5 text-xs border border-aura-border rounded-lg"
                    >
                      Mark listed
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <HelperConnectWizard
        open={wizardOpen}
        row={row}
        channel={channel}
        handle={helperHandle}
        onHandle={setHelperHandle}
        busy={busy}
        onClose={() => setWizardOpen(false)}
        onAllow={connectHelper}
      />
    </div>
  );
}

export default function Connections() {
  const { channel } = useParams();
  return channel ? <ConnectionDetail /> : <ConnectionCards />;
}
